import { action, computed, observable } from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';

import { AppManager, Variables, Input } from 'core/index';

import { FormStore } from 'libs/forms/app';

import { modalStore } from 'apps/modals/app';
import { clientStore } from 'apps/client/app';

import { ContributionForm } from 'apps/repository/views/contributionForm';
import { conf } from 'apps/repository/config.yaml';
import { ConflictsArrayApp, ConflictModel } from 'apps/repository/app';
import { ProjectApp, ProjectModel } from 'apps/project/app';
import {
  ConflictsQuery,
  CommitContributionMutation,
} from 'apps/repository/gql';

export class ConflictsManager extends AppManager {
  @observable activeConflict = 0;
  @observable resolution = observable.map();
  @observable contributionTitle = '';
  @observable contributionDescription = '';

  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('conflicts', ConflictsArrayApp);
    this.AppRef('project', ProjectApp);

    this.variables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
    });

    this.variables.onChange(['space', 'slug'], this.ConflictsQuery);
  }

  @computed
  get loading() {
    return this.queries.get('conflicts')?.loading;
  }

  @computed
  get allConflictsResolved() {
    return this.resolution.size >= this.conflicts.length;
  }

  @action
  ConflictsQuery = () => {
    const q = new ConflictsQuery(this, {
      queryKey: 'conflicts',
      onSysErr: this.onQError,
      fetchPolicy: 'no-cache',
    });
    q.process(
      conflicts => {
        if (conflicts !== null) {
          return conflicts.map(conflict => ConflictModel.Load(conflict));
        }
        return [];
      },
      this.UpdateConflicts,
      { subPath: 'conflicts' },
    );
    q.process(project => ProjectModel.Load(project), this.UpdateProject);
    q.query();
  };

  @action
  UpdateConflicts = conflictModels => {
    this.conflicts = conflictModels;
    const repoAppManager = clientStore.context.get('repoAppManager');
    if (!this.loading && this.conflicts.apps.length === 0) {
      // - when we pick the remote, then that will be committed, but it is
      // by another user
      // - our staging area will still be there, so we need to reset it to
      // clean it up then
      repoAppManager.CancelContribution();

      // - both conflicts model and repository model have a conflicts list,
      // so after conflicts resolution we need to update the repository state
      repoAppManager.NewRepoQuery(() => {
        clientStore.routerStore.navigate(
          `${clientStore.routerStore.route.name.replace(
            '.conflicts',
            '.files',
          )}`,
          this.params.toJSON(),
          { replace: true },
        );
      });
    } else {
      clientStore.context.set('conflictsManager', this);
    }
  };

  @action UpdateProject = projectModel => {
    this.project = projectModel;
  };

  @action
  SetActiveConflict = index => {
    this.activeConflict = this.apps[index];
  };

  @action
  SetResolution = (actionType, conflict, fileId, conflictIndex) => {
    if (actionType === 'pick') {
      this.resolution.set(conflict.path, fileId);
    } else if (actionType === 'delete') {
      this.resolution.set(conflict.path, null, conflictIndex);
    }
    conflict.SetMarkedResolved(true);
    if (this.conflicts.length !== conflictIndex + 1) {
      this.NextConflict(conflictIndex);
    } else {
      this.Finalise();
    }
  };

  @action
  NextConflict = currentConflictIndex => {
    this.activeConflict = currentConflictIndex + 1;
  };

  SetActiveConflict = conflictIndex => {
    this.activeConflict = conflictIndex;
  };

  @action
  Finalise = () => {
    if (this.allConflictsResolved) {
      this.form = new FormStore(this, conf, 'contributionForm');
      modalStore.SetModalComponent(ContributionForm, { manager: this });
    }
  };

  @action
  CommitContribution = () => {
    // - repo manager set committing = false before it redirects here,
    // we set it to true to disable the buttons while commit is ongoing,
    // it will be set back to false once either repo.OnCommitError or
    // repo.OnCommitSuccess is called
    const repoAppManager = clientStore.context.get('repoAppManager');

    const resObj = this.resolution.toJSON();
    const input = new Input({
      commitData: {
        projectId: this.project.id,
        title: this.contributionTitle,
        description: this.contributionDescription,
        conflictsParent: this.project.conflictsParent,
        conflictsResolution: Object.keys(resObj).map(res => {
          return {
            path: res,
            pickedFileId: resObj[res],
          };
        }),
      },
    });
    const options = {
      onSuccess: this.onCommitSuccess,
      onSysErr: this.onCommitError,
      onUserErr: this.onCommitError,
    };
    const mutation = new CommitContributionMutation(input, null, options, true);
    repoAppManager.committing = true;
    mutation.mutate();
    return mutation;
  };

  @action
  onCommitSuccess = result => {
    this.ConflictsQuery();
    // - when we commit a conflict resolution, the commit can come back with
    // more conflicts. So before calling OnCommitSuccess and all that, we check
    // if there are more conflicts.
    // - ConflictsQuery will update the conflict page state if there are indeed
    // conflicts, and then those can be dealt with, we just need to prevent
    // finishing the commit before we're done
    if (result.conflicts.length === 0) {
      this.form.clearDraft();
      modalStore.Close();
      Alert.success(i18next.t('Conflict resolution successful'));

      const repoAppManager = clientStore.context.get('repoAppManager');
      repoAppManager.OnCommitSuccess();
    }
  };

  @action
  onCommitError = error => {
    const repoAppManager = clientStore.context.get('repoAppManager');
    repoAppManager.OnCommitError(error);
  };
}
